import React from 'react';
import { Bell, Network, Radar } from 'lucide-react';
import { ViewMode } from '../../domain/types';
import { BrandIcon } from '../../ui/common';
import type { SidebarNavSource } from './sidebarModel';

function SectionLabel({ label, show }: { label: string; show: boolean }) {
  if (!show) return null;
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'var(--z-dim)',
        padding: '4px 10px',
      }}
    >
      {label}
    </div>
  );
}

function NavCountBadge({ count, active }: { count: number; active: boolean }) {
  return (
    <span
      style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 10.5,
        fontWeight: 700,
        padding: '1px 6px',
        borderRadius: 99,
        background: active ? 'var(--z-accent-border)' : 'var(--z-fill-2)',
        color: active ? 'var(--z-accent-ink)' : 'var(--z-dim)',
      }}
    >
      {count}
    </span>
  );
}

function navButtonStyle(active: boolean, showSidebarLabels: boolean): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: showSidebarLabels ? '8px 12px' : '9px 0',
    justifyContent: showSidebarLabels ? 'flex-start' : 'center',
    borderRadius: 10,
    background: active ? 'var(--z-accent-soft)' : 'transparent',
    color: active ? 'var(--z-accent-ink)' : 'var(--z-text-2)',
    fontWeight: active ? 600 : 500,
    border: 'none',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    transition: 'all 0.15s ease',
  };
}

export interface SidebarNavSectionsProps {
  showSidebarLabels: boolean;
  view: ViewMode;
  radarCount: number;
  notificationsCount: number;
  navSources: SidebarNavSource[];
  onNavigateToView: (view: ViewMode) => void;
}

export const SidebarNavSections: React.FC<SidebarNavSectionsProps> = ({
  showSidebarLabels,
  view,
  radarCount,
  notificationsCount,
  navSources,
  onNavigateToView,
}) => {
  const radarActive = view === 'radar';
  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <SectionLabel label="Panel" show={showSidebarLabels} />
        <button
          onClick={() => onNavigateToView('radar')}
          style={navButtonStyle(radarActive, showSidebarLabels)}
          title="Radar"
        >
          <Radar
            size={18}
            strokeWidth={2.2}
            aria-hidden="true"
            style={{ color: radarActive ? 'var(--z-accent)' : 'var(--z-muted-2)' }}
          />
          {showSidebarLabels ? (
            <>
              <span style={{ flex: '1 1 auto', fontSize: 13 }}>Radar</span>
              <NavCountBadge count={radarCount} active={radarActive} />
            </>
          ) : null}
        </button>
        <button
          onClick={() => onNavigateToView('notifications')}
          className={notificationsCount > 0 ? 'z-notifications-nav-button--unread' : undefined}
          style={navButtonStyle(view === 'notifications', showSidebarLabels)}
          title="Notificaciones"
          aria-label={`Notificaciones${notificationsCount > 0 ? `, ${notificationsCount} nuevas` : ''}`}
        >
          <Bell
            size={18}
            strokeWidth={2.2}
            aria-hidden="true"
            style={{ color: view === 'notifications' ? 'var(--z-accent)' : 'var(--z-muted-2)' }}
          />
          {showSidebarLabels ? (
            <>
              <span style={{ flex: '1 1 auto', fontSize: 13 }}>Notificaciones</span>
              {notificationsCount > 0 ? (
                <NavCountBadge count={notificationsCount} active={view === 'notifications'} />
              ) : null}
            </>
          ) : notificationsCount > 0 ? (
            <span className="z-notifications-nav-dot" aria-hidden="true" />
          ) : null}
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <SectionLabel label="Capacidades" show={showSidebarLabels} />
        {navSources.map((s) => {
          const active = view === s.id;
          return (
            <button
              key={s.id}
              onClick={() => onNavigateToView(s.id)}
              style={navButtonStyle(active, showSidebarLabels)}
              title={s.providerLabel}
              aria-label={`${s.label}, datos de ${s.providerLabel}`}
            >
              {s.id === 'wiki' ? (
                <Network
                  size={18}
                  strokeWidth={2.1}
                  aria-hidden="true"
                  style={{ color: active ? 'var(--z-accent)' : 'var(--z-accent-3)' }}
                />
              ) : (
                <BrandIcon source={s.brand} size={17} />
              )}
              {showSidebarLabels ? (
                <>
                  <span style={{ flex: '1 1 auto', fontSize: 13 }}>{s.label}</span>
                  <NavCountBadge count={s.count} active={active} />
                </>
              ) : null}
            </button>
          );
        })}
      </div>
    </>
  );
};
